import { useCallback, useEffect, useMemo, useState } from "react";
import { Tx4Shift } from "../../features/Tx4Shift";
import {
  Banner,
  ChainPickerCard,
  ExpandableFees,
  Skeleton,
  Spinner,
  Button,
  ButtonVariants,
  ButtonSizes,
  PageLayout,
  Navigation,
} from "@src/components";
import { AmountPicker } from "@src/features";
import { usePrivySmartWallet, useSwapContext } from "@src/contexts";
import { SuccessView } from "../SuccessView";
import { useDebounce, useTokenBalance } from "@src/hooks";
import { Fees, RoutePath } from "@src/interfaces";
import { calculateFees } from "@src/utils";
import { BackArrowIcon, CashIcon, CloseIcon } from "@src/assets";
import { useNavigate } from "react-router-dom";

enum DepositState {
  Init = "Init",
  Shift4 = "Shift4",
  Success = "Success",
}
export const DepositView = () => {
  const navigate = useNavigate();
  const {
    cashToken,
    fetchAllBalances,
    isFetchingBalances,
    isFetchingRoute,
    error,
    errorDetails,
    fetchShift4Quote,
    shift4Fees,
    shift4TokenAmount,
    isMultichain,
    chain,
  } = useSwapContext();
  const cashBalances = useTokenBalance({ token: cashToken });
  const [amount, setAmount] = useState("");
  const [depositState, setDepositState] = useState(DepositState.Init);
  const [showCloseButton, setShowCloseButton] = useState(false);
  const [showBackButton, setShowBackButton] = useState(true);
  const { smartWalletAddress } = usePrivySmartWallet();

  const { debounce: fetchQuoteDebounce } = useDebounce({
    delayMs: 200,
  });

  useEffect(() => {
    if (Number(amount) > 0) {
      fetchQuoteDebounce(() => fetchShift4Quote(amount));
    }
  }, [fetchShift4Quote, fetchQuoteDebounce, amount]);

  const gasFees: Fees = useMemo(() => {
    const { platformFee, networkFee, onrampFee } = calculateFees({
      route: undefined,
      cashToken,
      shift4Fees,
    });

    return {
      platformFee: platformFee,
      networkFee: networkFee,
      onrampFee: onrampFee,
    };
  }, [shift4Fees, cashToken]);

  const deposit = useCallback(() => {
    setDepositState(DepositState.Shift4);
  }, []);

  useEffect(() => {
    if (depositState === DepositState.Init) {
      setShowCloseButton(false);
      setShowBackButton(true);
    } else {
      setShowCloseButton(true);
      setShowBackButton(false);
    }
  }, [depositState]);

  return (
    <PageLayout
      navigation={
        <Navigation
          leftItems={[
            {
              content: showBackButton && <BackArrowIcon />,
              path: RoutePath.Wallet,
            },
            {
              content: "Deposit",
              disabled: true,
              path: RoutePath.Deposit,
            },
          ]}
          rightItems={
            showCloseButton
              ? [
                  {
                    content: <CloseIcon />,
                    onClick: () => navigate(RoutePath.Wallet),
                  },
                ]
              : []
          }
        />
      }
    >
      <>
        {depositState === DepositState.Init && (
          <div className="flex flex-col gap-2 h-full">
            {isMultichain && <ChainPickerCard chain={chain} />}
            <AmountPicker
              amount={amount}
              setAmount={setAmount}
              balance={cashBalances.abbreviated}
              isBalanceLoading={isFetchingBalances || !smartWalletAddress}
              helperButtonsType="dollar"
            />
            <ExpandableFees
              gasFees={gasFees}
              isFetchingRoute={isFetchingRoute}
            />
            <div className="flex flex-col flex-1 justify-end gap-2">
              {amount && (
                <>
                  {errorDetails ? (
                    <Banner>
                      <div className="flex flex-col items-start text-left">
                        {errorDetails}
                      </div>
                    </Banner>
                  ) : (
                    <>
                      {!error && (
                        <div className="flex flex-col gap-1">
                          <div className="text-md text-text_primary text-center">
                            {"You'll get:"}
                          </div>
                          <div className="flex rounded-[12px] bg-theme_color bg-opacity-20">
                            <div className="flex items-center justify-center gap-1 p-3 w-full">
                              {isFetchingRoute ? (
                                <Skeleton className="w-[84px] h-[26px]" />
                              ) : (
                                <div className="font-bold text-text_primary text-2xl">
                                  ${shift4TokenAmount}
                                </div>
                              )}
                              <CashIcon className="w-6 h-6" />
                              <div className="font-medium text-text_primary text-2xl text-opacity-80">
                                Cash
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
              <Button
                variant={ButtonVariants.Primary}
                disabled={
                  amount.length === 0 ||
                  !!error ||
                  !!errorDetails ||
                  isFetchingRoute
                }
                onClick={deposit}
                size={ButtonSizes.Large}
              >
                {isFetchingRoute ? (
                  <div className="flex justify-center items-center gap-2">
                    <div>Loading</div>
                    <Spinner className="w-4 h-4 fill-white" />
                  </div>
                ) : (
                  "Confirm"
                )}
              </Button>
            </div>
          </div>
        )}
        {depositState === DepositState.Shift4 && (
          <Tx4Shift
            amount={amount}
            onlyDeposit={true}
            onOrderComplete={() => {
              setDepositState(DepositState.Success);
              fetchAllBalances();
            }}
          />
        )}
        {depositState === DepositState.Success && (
          <SuccessView
            title={"Deposit successful"}
            amount={shift4TokenAmount}
          />
        )}
      </>
    </PageLayout>
  );
};
